import { isQueryOnlyNavigation } from '~/utils/isQueryOnlyNavigation';

/**
 * Sends the visitor of a restricted story to the redirect target of that story.
 *
 * The middleware runs on each navigation and finds the story by its path. In the browser, it skips a
 * navigation that changes only the query or the hash of the page.
 */
export default defineNuxtRouteMiddleware(async (to, from) => {
    /** The restriction of a story depends on its path. A change of the query cannot change it. */
    if (isQueryOnlyNavigation(to, from)) return;

    const { isDraftModeEnabled } = useDraftMode();
    if (isDraftModeEnabled.value) return;
    if (to.path.startsWith('/api/') || to.path.startsWith('/_nuxt/') || to.path.startsWith('/__webpack_hmr')) return;
    if (to.meta.skipStoryblokLockCheck) return;

    const { rawPath } = useCleanStoryblokPath(to);
    const { data: story, error } = await useStory<PageComponentStoryblok>(rawPath, null, {
        disableFatalErrors: true,
        route: to
    });
    if (error.value) return;

    const restricted = story.value?.content?.__pageRestricted;
    const target = useStoryblokUrl(story.value?.content?.__redirectTarget, to);

    if (!restricted || !target) return;

    const external = /^https?:\/\//i.test(target);

    const normalizedTarget = external ? target : `/${target.replace(/^\/+/, '')}`;
    const normalizedCurrent = `/${to.path.replace(/^\/+/, '')}`;

    if (!external && normalizedCurrent === normalizedTarget) {
        console.warn('[redirectLockedPages] prevented self-redirect loop:', normalizedTarget);
        return;
    }

    log.debug(
        `[redirectLockedPages] Redirecting from ${normalizedCurrent} to ${normalizedTarget} (external: ${external})`
    );
    return navigateTo(normalizedTarget, { replace: true, external });
});
